import {
	EditableCell,
	editableRowSx,
} from "@/shared/components/editable-table/EditableCell";
import {
	ACTIONS_CELL_SX,
	RowActions,
} from "@/shared/components/editable-table/RowActions";
import { useEditableRow } from "@/shared/components/editable-table/useEditableRow";
import { getMomentTz } from "@dexteel/mesf-core";
import { PushPinOutlined } from "@mui/icons-material";
import { Box, TableCell, TableRow, Tooltip } from "@mui/material";
import { ReactNode, useState } from "react";
import { NumberCell } from "../../shared/components/table/Cell";
import { CellValue } from "./CellValue";
import {
	GasEntryMeter,
	GasEntryTarget,
	GasEntryTargetZone,
} from "../models/GasEntry";
import {
	GasEntryDayRow,
	getBreakdownMcf,
	getEffectiveHeatValue,
	getTotalMcf,
	getTotalMmbtu,
	isHeatValueOverridden,
	isHeatValueStored,
} from "../utils/gasEntryRows";

const moment = getMomentTz();

const format = (value: number | null, digits: number) =>
	value === null
		? ""
		: value.toLocaleString("en-US", { maximumFractionDigits: digits });

const getZoneSplitTooltip = (
	targetName: string,
	breakdown: number | null,
	zones: GasEntryTargetZone[],
): ReactNode => {
	if (zones.length === 0) return "";

	return (
		<Box>
			<Box sx={{ fontWeight: 600, mb: 0.25 }}>{targetName} by furnace zone</Box>
			{zones.map((zone) => (
				<Box key={zone.GasZoneId}>
					{zone.Zone} · {format(zone.SharePercent, 1)}%
					{breakdown !== null &&
						` · ${format((breakdown * zone.SharePercent) / 100, 1)} MCF`}
				</Box>
			))}
			<Box sx={{ mt: 0.5, opacity: 0.75 }}>
				Shares of what the furnace burned this month, as tracked.
			</Box>
		</Box>
	);
};

const getHeatValueTooltip = (row: GasEntryDayRow): string => {
	const fallback = row.billHeatValue;
	const formatDate = (date: string | null) =>
		moment(date, "YYYY-MM-DD").format("MM/DD/YY");
	const sourceLabel =
		fallback === null
			? null
			: fallback.GasBillId === null
				? `the reading of ${formatDate(fallback.SourceEntryDate)}`
				: `the bill of ${formatDate(fallback.BillPeriodStart)} → ${formatDate(fallback.BillPeriodEnd)}`;

	if (isHeatValueOverridden(row))
		return sourceLabel === null
			? "Entered for this day only."
			: `Entered for this day only; ${sourceLabel} says ${format(fallback?.HeatValueBtuPerCf ?? null, 2)}.`;

	if (isHeatValueStored(row) && fallback?.GasBillId == null)
		return "Entered for this day; no gas bill entered yet.";

	if (fallback === null)
		return "No gas bill entered yet: enter the heat value for this day.";

	if (fallback.GasBillId === null)
		return `From ${sourceLabel}; no gas bill entered yet. Type a value to change it for this day only.`;

	return fallback.IsBillCovering
		? `From ${sourceLabel}. Type a value to change it for this day only.`
		: `From ${sourceLabel}, the closest bill with a heat value; it does not cover this day. Type a value to change it for this day only.`;
};

interface GasEntryRowProps {
	row: GasEntryDayRow;
	meters: GasEntryMeter[];
	targets: GasEntryTarget[];
	zonesByTargetId: Record<number, GasEntryTargetZone[]>;
	trackedMcf: number | null;
	trackedMcfByTargetId: Record<number, number>;
	isEditing: boolean;
	isLocked: boolean;
	isSaving: boolean;
	onMeterPatch: (
		gasMeterId: number,
		field: "uncorrectedVolume" | "pressurePsi" | "temperatureF",
		value: number | null,
	) => void;
	onHeatValuePatch: (value: number | null) => void;
	onEdit: () => void;
	onCancel: () => void;
	onSave: () => void;
	onDelete: () => void;
}

export const GasEntryRow = ({
	row,
	meters,
	targets,
	zonesByTargetId,
	trackedMcf,
	trackedMcfByTargetId,
	isEditing,
	isLocked,
	isSaving,
	onMeterPatch,
	onHeatValuePatch,
	onEdit,
	onCancel,
	onSave,
	onDelete,
}: GasEntryRowProps) => {
	const [isTotalHovered, setTotalHovered] = useState(false);

	const isEmpty =
		row.gasEntryId === null &&
		Object.values(row.valuesByMeterId).every(
			(values) =>
				values.uncorrectedVolume === null &&
				values.pressurePsi === null &&
				values.temperatureF === null,
		);
	const blockedReason = isSaving
		? "Still loading, try again in a moment"
		: isEmpty
			? "Nothing to save yet"
			: null;

	const editable = useEditableRow<HTMLTableRowElement>({
		isEditing,
		isLocked,
		canStartEdit: !isSaving,
		blockedReason,
		onEdit,
		onCancel,
		onSave,
	});

	const totalMcf = getTotalMcf(row, meters);
	const isBelowTracked =
		totalMcf !== null &&
		trackedMcf !== null &&
		Math.round(totalMcf * 10) < Math.round(trackedMcf * 10);
	const totalMmbtu = getTotalMmbtu(row, meters);

	const tip = (title: ReactNode): ReactNode => (isLocked ? "" : title);
	const totalTitle = isBelowTracked
		? `Total MCF is lower than the tracked gas for the day (${format(trackedMcf, 1)} MCF)`
		: tip(
				"Each meter corrected to base conditions from its own pressure and temperature, then added up.",
			);

	const heatValue = getEffectiveHeatValue(row);
	const isHeatValueFromBill = !isHeatValueStored(row) && heatValue !== null;
	const isHeatValueEntered = isHeatValueOverridden(row) && heatValue !== null;
	const isHeatValueFromReading =
		isHeatValueFromBill && row.billHeatValue?.GasBillId === null;
	const isHeatValueFromLatestBill =
		isHeatValueFromBill &&
		!isHeatValueFromReading &&
		row.billHeatValue !== null &&
		!row.billHeatValue.IsBillCovering;

	const breakdownOf = (targetId: number) =>
		getBreakdownMcf(row, targetId, totalMcf, trackedMcfByTargetId);

	return (
		<TableRow
			{...editable.rowProps}
			data-test-id={`gas-entry-row-${row.date}`}
			sx={editableRowSx({ isEditing, isLocked })}
		>
			<TableCell sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
				{moment(row.date, "YYYY-MM-DD").format("ddd MM/DD")}
			</TableCell>
			{meters.flatMap((meter) => {
				const values = row.valuesByMeterId[meter.GasMeterId] ?? {
					uncorrectedVolume: null,
					pressurePsi: null,
					temperatureF: null,
				};
				return [
					<EditableCell
						key={`uv-${meter.GasMeterId}`}
						data-test-id={`uv-${meter.MeterCode}`}
						sx={{ textAlign: "right" }}
						isEditing={isEditing}
						editor={
							<NumberCell
								value={values.uncorrectedVolume}
								min={0}
								integerDigits={15}
								decimals={3}
								onChange={(value) =>
									onMeterPatch(meter.GasMeterId, "uncorrectedVolume", value)
								}
							/>
						}
					>
						<CellValue>{format(values.uncorrectedVolume, 0)}</CellValue>
					</EditableCell>,
					<EditableCell
						key={`psi-${meter.GasMeterId}`}
						data-test-id={`psi-${meter.MeterCode}`}
						sx={{ textAlign: "right" }}
						isEditing={isEditing}
						editor={
							<NumberCell
								value={values.pressurePsi}
								min={0}
								integerDigits={6}
								decimals={3}
								onChange={(value) =>
									onMeterPatch(meter.GasMeterId, "pressurePsi", value)
								}
							/>
						}
					>
						<CellValue>{format(values.pressurePsi, 1)}</CellValue>
					</EditableCell>,
					<EditableCell
						key={`temp-${meter.GasMeterId}`}
						data-test-id={`temp-${meter.MeterCode}`}
						sx={{ textAlign: "right" }}
						isEditing={isEditing}
						editor={
							<NumberCell
								value={values.temperatureF}
								integerDigits={5}
								decimals={2}
								onChange={(value) =>
									onMeterPatch(meter.GasMeterId, "temperatureF", value)
								}
							/>
						}
					>
						<CellValue>{format(values.temperatureF, 1)}</CellValue>
					</EditableCell>,
				];
			})}
			<EditableCell
				data-test-id="heat-value"
				sx={{
					textAlign: "right",
					...(isHeatValueFromBill ? { color: "#7a7a7a" } : null),
				}}
				isEditing={isEditing}
				editor={
					<NumberCell
						value={row.heatValueBtuPerCf}
						min={0}
						integerDigits={8}
						decimals={4}
						onChange={onHeatValuePatch}
					/>
				}
			>
				<Tooltip title={tip(getHeatValueTooltip(row))}>
					<Box
						component="span"
						data-test-id="heat-value-source"
						data-source={
							isHeatValueStored(row)
								? "day"
								: isHeatValueFromReading
									? "reading"
									: isHeatValueFromLatestBill
										? "latest-bill"
										: isHeatValueFromBill
											? "bill"
											: undefined
						}
						sx={{
							alignItems: "center",
							display: "flex",
							gap: 0.25,
							justifyContent: "flex-end",
						}}
					>
						{isHeatValueEntered && (
							<PushPinOutlined
								aria-hidden="true"
								sx={{ fontSize: 11, mr: "auto" }}
							/>
						)}
						<CellValue>{format(heatValue, 2)}</CellValue>
					</Box>
				</Tooltip>
			</EditableCell>
			<Tooltip
				title={totalTitle}
				open={(isEditing && isBelowTracked) || isTotalHovered}
				onOpen={() => setTotalHovered(totalTitle !== "")}
				onClose={() => setTotalHovered(false)}
				arrow={isBelowTracked}
				placement={isBelowTracked ? "top" : "bottom"}
				slotProps={{
					tooltip: {
						sx: isBelowTracked
							? { bgcolor: "warning.main", color: "warning.contrastText" }
							: {},
					},
					arrow: { sx: isBelowTracked ? { color: "warning.main" } : {} },
				}}
			>
				<TableCell
					data-test-id="total-mcf"
					sx={{
						color: isBelowTracked ? "warning.dark" : "#7a7a7a",
						fontWeight: isBelowTracked ? 600 : undefined,
						backgroundColor: "grey.100",
						textAlign: "right",
					}}
				>
					<CellValue>{format(totalMcf, 1)}</CellValue>
				</TableCell>
			</Tooltip>
			<Tooltip
				title={tip(
					heatValue === null
						? "Enter what the gas carried that day and this fills in."
						: `The day's Mcf at ${format(heatValue, 2)} BTU per cubic foot.`,
				)}
			>
				<TableCell
					data-test-id="total-mmbtu"
					sx={{
						color: "#7a7a7a",
						backgroundColor: "grey.100",
						textAlign: "right",
					}}
				>
					<CellValue>{format(totalMmbtu, 1)}</CellValue>
				</TableCell>
			</Tooltip>
			{targets.map((target) => (
				<TableCell
					key={`pct-${target.TargetId}`}
					data-test-id={`share-${target.TargetCode}`}
					sx={{
						color: "#7a7a7a",
						backgroundColor: "grey.100",
						textAlign: "right",
					}}
				>
					<CellValue>
						{format(row.percentByTargetId[target.TargetId] ?? null, 2)}
					</CellValue>
				</TableCell>
			))}
			{targets.map((target) => {
				const breakdown = breakdownOf(target.TargetId);
				return (
					<Tooltip
						key={`brk-${target.TargetId}`}
						title={tip(
							getZoneSplitTooltip(
								target.TargetName,
								breakdown,
								zonesByTargetId[target.TargetId] ?? [],
							),
						)}
					>
						<TableCell
							data-test-id={`breakdown-${target.TargetCode}`}
							sx={{
								color: "#7a7a7a",
								backgroundColor: "grey.100",
								textAlign: "right",
							}}
						>
							<CellValue>{format(breakdown, 1)}</CellValue>
						</TableCell>
					</Tooltip>
				);
			})}
			<TableCell sx={ACTIONS_CELL_SX}>
				<RowActions
					isEditing={isEditing}
					isLocked={isLocked}
					canEdit={editable.canStartEdit}
					editTitle={row.gasEntryId === null ? "Fill in this day" : "Edit"}
					deleteTitle="Delete this day"
					canDelete={row.gasEntryId !== null}
					blockedReason={blockedReason}
					isReasonShown={editable.isReasonShown}
					testId="day"
					onEdit={() => editable.startEdit()}
					onDelete={onDelete}
					onCancel={onCancel}
					onSave={editable.save}
				/>
			</TableCell>
		</TableRow>
	);
};
